import { API_ENDPOINTS } from '@/config/apiEndpoints';
import { notificationsApiClient } from '@/shared/utils/api/apiClient';
import { parseOrThrow } from '@/shared/utils/api/parseResponse';
import { ApiError } from '@/shared/types/api.types';
import { toWatch, watchListSchema, watchSchema } from '../api/watchContracts';
import type { FollowTarget, Watch } from '../types/watch.types';

class WatchService {
  async list(): Promise<readonly Watch[]> {
    const payload = await notificationsApiClient.get<unknown>(API_ENDPOINTS.notifications.watches);
    return parseOrThrow(watchListSchema, payload, 'list of follows').map(toWatch);
  }

  /** Following something already followed returns the existing follow. */
  async follow(target: FollowTarget): Promise<Watch> {
    const payload = await notificationsApiClient.post<unknown>(API_ENDPOINTS.notifications.watches, {
      model_id: target.modelId,
      kind: target.kind,
      unique_id: target.uniqueId,
      label: target.label,
    });
    return toWatch(parseOrThrow(watchSchema, payload, 'follow'));
  }

  /** Unfollowing something already gone (another tab, say) is not an error: it is gone. */
  async unfollow(watchId: string): Promise<void> {
    try {
      await notificationsApiClient.delete<unknown>(API_ENDPOINTS.notifications.watch(watchId));
    } catch (error) {
      if (!(error instanceof ApiError && error.kind === 'notFound')) throw error;
    }
  }

  /**
   * Brings the address Notifications emails up to date with this session's token, so a
   * verification or address change made since the last follow is not missed.
   */
  async refreshSubscriber(): Promise<void> {
    await notificationsApiClient.put<unknown>(API_ENDPOINTS.notifications.me);
  }
}

export const watchService = new WatchService();
